import { useState } from 'react'

import { t } from '../../copy'
import { T } from '../../copy/text'
import { ApiFailure } from '../../app/errors'
import { modelCellFor, parseDisableImpact, type DisableImpact } from '../lulu/wire'
import {
  LULU_RESPONSIBILITIES,
  useAgentLibrary,
  useCreateAgent,
  useDisableAgent,
  useUpdateAgent,
  useSuggestAgentSetup,
  type LuluAgent,
  type LuluResponsibility,
} from './queries'

/**
 * 屏 `agents`「Agent 库」（设计稿 LuluV3 · agents / agentAdd）。
 *
 * # 这一屏的主张只有一句
 *
 * **你派活给 Agent，不是派给模型。** 同一个模型可以支撑好几个 Agent——
 * 它们名字不同、职责不同、写域不同、记忆不通。所以第一块先把这层关系
 * 说清楚，而不是直接甩一张模型表。
 *
 * # 少了两列，是刻意的
 *
 * 设计稿的表有七列，这里只画五列：**「能改哪儿」（写域）与「记忆」
 * 后端现在给不出来**——`GET /agents` 回的是
 * `{id, display_name, display_name_source, avatar_ref, role, scope,
 *   model_policy, model_status, model_status_reason, revision, disabled}`。
 *
 * 把列画出来再填一个编的值，比不画那一列糟得多：一张看起来完整的表
 * 会让人据此做决定，而其中两列是假的。缺口记在 PAGES.md。
 *
 * # 写「停用」不写「删除」
 *
 * 后端 `DELETE /agents/{id}` 是**软删且幂等**。写「删除」会让人以为
 * 它做过的事也跟着没了，而那些证据都还在。
 */
export function AgentsScreen() {
  const library = useAgentLibrary()
  const create = useCreateAgent()
  const disable = useDisableAgent()
  /**
   * 正在确认停用的那一位，以及后端回的四档影响面。
   *
   * `null` = 没人在确认。**影响面与「哪一位」放在一起**，不是两个 state：
   * 分开存的话，两次快速点击会让第二位的名字配上第一位的影响面，
   * 而那份数字看着完全正常。
   */
  const [confirming, setConfirming] = useState<{
    readonly agent: LuluAgent
    readonly impact: DisableImpact | null
  } | null>(null)
  const [disableFailed, setDisableFailed] = useState(false)
  const [adding, setAdding] = useState(false)
  /**
   * 正在改的那一位。**存整行而不是只存 id**：标题里要他的名字，
   * 而按 id 回列表里再查一遍，会在列表刚好重取过的那一刻查空。
   */
  const [editing, setEditing] = useState<LuluAgent | null>(null)
  const [name, setName] = useState('')
  const [role, setRole] = useState('')
  const [responsibilities, setResponsibilities] = useState<readonly LuluResponsibility[]>([])
  /**
   * 「让 Lulu 配」那一下的结果。
   *
   * **执行器与标签放在同一个 state 里**，不是两个：分开存的话，
   * 连点两次（第二次配不出执行器）会让新的标签配上旧的执行器名字，
   * 而那一行看着完全正常。这与本屏 `confirming` 那处是同一个道理。
   *
   * `null` = 还没配过。配过但一个标签都没配出来是
   * `{ tags: [], executor: null }`——那一档要说的话不一样。
   */
  const update = useUpdateAgent()
  const [filled, setFilled] = useState<{
    readonly tags: readonly LuluResponsibility[]
    readonly executor: { readonly id: string; readonly name: string } | null
  } | null>(null)
  const suggest = useSuggestAgentSetup()

  const agents: readonly LuluAgent[] = library.data?.agents ?? []

  /** 收表并清干净。**两条路共用**，不然「改」那条会把上一次的草稿留给下一次。 */
  function closeDialog() {
    setAdding(false)
    setEditing(null)
    setName('')
    setRole('')
    setResponsibilities([])
    setFilled(null)
  }

  /**
   * 打开「改」，把这一位现在的样子预填进去。
   *
   * **预填的是他现在真有的东西**，不是空表：一张空表在这里等于
   * 「保存 = 把他清空」，而那正好是这条路要修的那种坑
   * （见 team 那边 global 成员的预填）。
   */
  function openEdit(agent: LuluAgent) {
    setAdding(false)
    setEditing(agent)
    setName(agent.display_name_source === 'user' ? agent.display_name : '')
    setRole(agent.role ?? '')
    setResponsibilities(agent.responsibilities as readonly LuluResponsibility[])
    setFilled(null)
  }

  return (
    <div data-testid="agents-screen" className="mx-auto w-full max-w-[1080px] px-5 py-6">
      {' '}
      {/* npc-token-allow: 设计稿 Agent 库最大宽 1080px */}
      <header className="mb-4 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="font-npc-display text-npc-section text-npc-lulu-t1">
            <T id="lulu_agents.title" />
            {library.data === undefined ? null : (
              <span data-testid="agents-count" className="ml-2 text-npc-caption text-npc-lulu-t3">
                <T id="lulu_agents.count" vars={{ count: String(agents.length) }} />
              </span>
            )}
          </h1>
          <p className="mt-1 text-npc-caption text-npc-lulu-t3">
            <T id="lulu_agents.sub" />
          </p>
        </div>
        <button
          type="button"
          data-testid="agents-add"
          onClick={() => {
            closeDialog()
            setAdding(true)
          }}
          className="h-[30px] shrink-0 rounded-npc-chip bg-npc-lulu-t1 px-3.5 text-npc-caption font-medium text-npc-lulu-bg-0" /* npc-token-allow: 设计稿桌面按钮高 30px */
        >
          <T id="lulu_agents.add" />
        </button>
      </header>
      {/* 第一块：先说清 Agent 和模型的关系。 */}
      <section className="mb-3 rounded-npc-chip border border-npc-lulu-line-2 bg-npc-lulu-bg-1 p-3.5">
        <p className="text-npc-meta text-npc-lulu-t4">
          <T id="lulu_agents.why_heading" />
        </p>
        <h2 className="mt-0.5 font-npc-display text-npc-section-sm text-npc-lulu-t1">
          <T id="lulu_agents.why_title" />
        </h2>
        <p className="mt-1 text-npc-caption leading-[1.7] text-npc-lulu-t2">
          {' '}
          {/* npc-token-allow: 设计稿正文行高 1.7 */}
          <T id="lulu_agents.why_body" />
        </p>
      </section>
      {adding || editing !== null ? (
        <AgentDialog
          mode={editing === null ? 'create' : 'edit'}
          name={name}
          role={role}
          responsibilities={responsibilities}
          onName={setName}
          onRole={setRole}
          onToggleResponsibility={(tag) => {
            setResponsibilities((cur) =>
              cur.includes(tag) ? cur.filter((x) => x !== tag) : [...cur, tag],
            )
            // 他自己动过手之后，那句「配好了：…」就不再描述屏幕上的东西了。
            // 留着它会变成一句过期的话，而过期的话比没有话更容易被信。
            setFilled(null)
          }}
          filled={filled}
          filling={suggest.isPending}
          fillFailed={suggest.isError}
          onFill={() => {
            suggest.mutate(
              { description: role, displayName: name },
              {
                onSuccess: (out) => {
                  const tags = (out.responsibilities ?? []) as readonly LuluResponsibility[]
                  // **覆盖而不是合并。** 合并的话，改一次描述再配一次，
                  // 上一次配错的标签会留在身上，而他以为自己重配过了。
                  setResponsibilities(tags)
                  setFilled({
                    tags,
                    executor:
                      out.executor_maintainer_id === undefined
                        ? null
                        : { id: out.executor_maintainer_id, name: out.executor_name ?? '' },
                  })
                },
              },
            )
          }}
          failed={editing === null ? create.isError : update.isError}
          busy={editing === null ? create.isPending : update.isPending}
          onCancel={() => {
            setAdding(false)
            setEditing(null)
            setFilled(null)
          }}
          onSubmit={() => {
            if (editing !== null) {
              update.mutate(
                {
                  agentId: editing.id,
                  displayName: name,
                  role,
                  responsibilities,
                  // 配出来的执行器一起交。**没配过就不动它**——
                  // 不传这个键服务端就不改绑定（PATCH 的语义），
                  // 而传一个空的会把他原来绑好的那个解掉。
                  ...(filled?.executor ? { executorMaintainerId: filled.executor.id } : {}),
                },
                { onSuccess: () => closeDialog() },
              )
              return
            }
            create.mutate(
              {
                displayName: name,
                role,
                responsibilities,
                // 配出来的执行器一起交。**不交的话这一整条就白做了**：
                // 标签配上了、执行器空着，那位 Agent 照样派不到活——
                // 而那正是 buger 当初的样子。
                ...(filled?.executor ? { executorMaintainerId: filled.executor.id } : {}),
              },
              { onSuccess: () => closeDialog() },
            )
          }}
        />
      ) : null}
      {library.isError ? (
        <p data-testid="agents-unreachable" className="text-npc-caption text-npc-lulu-warn">
          <T id="lulu_agents.unreachable" />
        </p>
      ) : library.data === undefined ? null : agents.length === 0 ? (
        <p data-testid="agents-empty" className="text-npc-caption text-npc-lulu-t3">
          <T id="lulu_agents.empty" />
        </p>
      ) : (
        <table data-testid="agents-table" className="w-full border-collapse text-left">
          <thead>
            <tr className="text-npc-meta text-npc-lulu-t4">
              <th className="py-1.5 font-normal">
                <T id="lulu_agents.col_agent" />
              </th>
              <th className="py-1.5 font-normal">
                <T id="lulu_agents.col_role" />
              </th>
              <th className="py-1.5 font-normal">
                <T id="lulu_agents.col_responsibilities" />
              </th>
              <th className="py-1.5 font-normal">
                <T id="lulu_agents.col_executor" />
              </th>
              <th className="py-1.5 font-normal">
                <T id="lulu_agents.col_model" />
              </th>
              <th className="py-1.5 font-normal">
                <T id="lulu_agents.col_scope" />
              </th>
              <th className="py-1.5 font-normal">
                <T id="lulu_agents.col_state" />
              </th>
              <th className="py-1.5 font-normal" />
            </tr>
          </thead>
          <tbody>
            {agents.map((a) => {
              const model = modelCellFor(a)
              return (
                <tr
                  key={a.id}
                  data-testid="agents-row"
                  data-agent={a.id}
                  data-disabled={a.disabled ? 'yes' : 'no'}
                  className="border-t border-npc-lulu-line-1 text-npc-caption text-npc-lulu-t2"
                >
                  <td data-testid="agents-row-name" className="py-2 text-npc-lulu-t1">
                    {a.display_name}
                  </td>
                  <td className="py-2">{a.role ?? ''}</td>
                  <td data-testid="agents-row-responsibilities" className="py-2">
                    {a.responsibilities.length === 0 ? (
                      <span className="text-npc-lulu-t4">—</span>
                    ) : (
                      <span className="flex flex-wrap gap-1">
                        {a.responsibilities.map((tag) => (
                          <span
                            key={tag}
                            data-resp={tag}
                            className="rounded-npc-inline bg-npc-lulu-bg-2 px-1.5 py-0.5 text-npc-meta text-npc-lulu-t2"
                          >
                            <T id={responsibilityCopyId(tag)} />
                          </span>
                        ))}
                      </span>
                    )}
                  </td>
                  <td
                    data-testid="agents-row-executor"
                    data-bound={a.executor_maintainer_id ? 'yes' : 'no'}
                    className="py-2 text-npc-meta"
                  >
                    {a.executor_maintainer_id ? (
                      <span className="text-npc-lulu-t2">
                        {a.executor_name ?? a.executor_maintainer_id}
                        {a.executor_provider ? (
                          <span className="ml-1 font-npc-mono text-npc-lulu-t4">
                            {a.executor_provider}
                            {a.executor_model ? ` / ${a.executor_model}` : ''}
                          </span>
                        ) : null}
                      </span>
                    ) : (
                      <span className="text-npc-lulu-t4">
                        <T id="lulu_agents.executor_unbound" />
                      </span>
                    )}
                  </td>
                  <td
                    data-testid="agents-row-model"
                    data-model-kind={model.kind}
                    className={
                      model.kind === 'id'
                        ? 'py-2 font-npc-mono text-npc-meta'
                        : 'py-2 text-npc-meta text-npc-lulu-t4'
                    }
                  >
                    {model.kind === 'id' ? model.id : <T id={model.id} />}
                  </td>
                  <td className="py-2">
                    <T
                      id={
                        a.scope === 'project'
                          ? 'lulu_agents.scope_project'
                          : 'lulu_agents.scope_account'
                      }
                    />
                  </td>
                  <td data-testid="agents-row-state" className="py-2">
                    <T
                      id={a.disabled ? 'lulu_agents.state_disabled' : 'lulu_agents.state_available'}
                    />
                  </td>
                  <td className="py-2 text-right">
                    {/* 「改配置」：设计稿 §agents 行操作里早就有这颗，
                        一直没有消费方。没有它，一位建的时候职责没勾上的
                        Agent 在界面上**再也补不上**——只能停用了重建。 */}
                    {a.disabled ? null : (
                      <button
                        type="button"
                        data-testid="agents-edit"
                        onClick={() => openEdit(a)}
                        className="mr-2 h-[26px] rounded-npc-chip border border-npc-lulu-line-2 px-2.5 text-npc-meta text-npc-lulu-t2" /* npc-token-allow: 设计稿次级小按钮高 26px */
                      >
                        <T id="lulu_agents.edit" />
                      </button>
                    )}
                    {a.disabled ? null : (
                      <button
                        type="button"
                        data-testid="agents-disable"
                        disabled={disable.isPending}
                        onClick={() => {
                          setDisableFailed(false)
                          disable.mutate(
                            { agentId: a.id },
                            {
                              // 409 = 手上还有正在进行的事。**不吞它**：
                              // 把四档说成人话，再给一颗「还是停用」。
                              // 上一版没有这一步，用户看到的是一个
                              // 没有出口的错误——那道门在界面上是死胡同。
                              onError: (err) => {
                                if (err instanceof ApiFailure && err.status === 409) {
                                  setConfirming({
                                    agent: a,
                                    impact: parseDisableImpact(err.details),
                                  })
                                  return
                                }
                                setDisableFailed(true)
                              },
                            },
                          )
                        }}
                        className="rounded-npc-inline px-2 py-0.5 text-npc-meta text-npc-lulu-t3 disabled:opacity-40"
                      >
                        <T id="lulu_agents.disable" />
                      </button>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}
      {/*
        停用确认。**四档分开说，不合成一个数字。**

        判据是 CLAUDE.md 那条「两件事如果要求用户做的下一个动作不同，
        就不许合成一种说法」：正在跑的要他安排接手；排队的会一直卡着；
        醒着的会被请出去；席位只是以后派不到它。
        合成一个「有 3 处受影响」，既可能是三件正在烧的活，
        也可能是三条早就结束的会话里的挂名。

        **每一档为 0 就不显示那一句**——把四个 0 都摆出来，
        真正要紧的那一档就淹在里面了。

        影响面解不出来时（后端换了形状 / 409 没带 details）只说
        「有东西挡着」那句标题，**不编四个 0**：一份四档全 0 的影响面
        说的是「停用不会打断任何东西」，那是会让人放心点下去的话。
      */}
      {confirming === null ? null : (
        <div
          data-testid="agents-disable-confirm"
          className="mt-3 rounded-npc-chip border border-npc-lulu-warn/40 bg-npc-lulu-warn/[0.06] p-3.5"
        >
          <p className="text-npc-caption font-medium text-npc-lulu-warn">
            <T id="lulu_workbench.disable.title" vars={{ agent: confirming.agent.display_name }} />
          </p>
          {confirming.impact === null ? null : (
            <ul data-testid="agents-disable-impact" className="mt-1.5 grid gap-1">
              {confirming.impact.runningItems > 0 ? (
                <li
                  data-testid="agents-impact-running"
                  className="text-npc-meta text-npc-lulu-warn"
                >
                  <T
                    id="lulu_workbench.disable.running"
                    vars={{ count: String(confirming.impact.runningItems) }}
                  />
                </li>
              ) : null}
              {confirming.impact.pendingItems > 0 ? (
                <li
                  data-testid="agents-impact-pending"
                  className="text-npc-meta text-npc-lulu-warn"
                >
                  <T
                    id="lulu_workbench.disable.pending"
                    vars={{ count: String(confirming.impact.pendingItems) }}
                  />
                </li>
              ) : null}
              {confirming.impact.activeSessions > 0 ? (
                <li data-testid="agents-impact-sessions" className="text-npc-meta text-npc-lulu-t3">
                  <T
                    id="lulu_workbench.disable.sessions"
                    vars={{ count: String(confirming.impact.activeSessions) }}
                  />
                </li>
              ) : null}
              {confirming.impact.rosterSeats > 0 ? (
                <li data-testid="agents-impact-seats" className="text-npc-meta text-npc-lulu-t3">
                  <T
                    id="lulu_workbench.disable.seats"
                    vars={{ count: String(confirming.impact.rosterSeats) }}
                  />
                </li>
              ) : null}
              {confirming.impact.conversationsTruncated ? (
                <li
                  data-testid="agents-impact-truncated"
                  className="text-npc-meta text-npc-lulu-t4"
                >
                  <T id="lulu_workbench.disable.conversations_truncated" />
                </li>
              ) : null}
            </ul>
          )}
          <div className="mt-2.5 flex flex-wrap items-center gap-2">
            <button
              type="button"
              data-testid="agents-disable-anyway"
              disabled={disable.isPending}
              onClick={() => {
                const agentId = confirming.agent.id
                disable.mutate(
                  { agentId, acknowledge: true },
                  {
                    onSuccess: () => setConfirming(null),
                    onError: () => setDisableFailed(true),
                  },
                )
              }}
              className="h-[30px] rounded-npc-chip bg-npc-lulu-t1 px-3 text-npc-caption font-medium text-npc-lulu-bg-0 disabled:opacity-50" /* npc-token-allow: 设计稿桌面按钮高 30px，整站 token 无此档 */
            >
              <T id="lulu_workbench.disable.confirm" />
            </button>
            <button
              type="button"
              data-testid="agents-disable-cancel"
              onClick={() => setConfirming(null)}
              className="h-[30px] rounded-npc-chip border border-npc-lulu-line-2 bg-npc-lulu-bg-2 px-3 text-npc-caption text-npc-lulu-t2" /* npc-token-allow: 设计稿桌面按钮高 30px，整站 token 无此档 */
            >
              <T id="lulu_workbench.disable.cancel" />
            </button>
          </div>
        </div>
      )}
      {disableFailed ? (
        <p data-testid="agents-disable-failed" className="mt-2 text-npc-caption text-npc-lulu-bad">
          <T id="lulu_workbench.disable.failed" />
        </p>
      ) : null}
      {agents.length === 0 ? null : (
        <p className="mt-2 text-npc-meta text-npc-lulu-t4">
          <T id="lulu_agents.disable_note" />
        </p>
      )}
    </div>
  )
}

/**
 * 把标签列成一串中文，顿号隔开。
 *
 * 用的是同一份 `lulu_agents.resp_*` 模板——**不在这里另写一份中文名**。
 * 两份名字分家的表现是：卡片上写「测试」，配好那句话里写「QA」，
 * 而他会以为那是两回事。
 */
function tagWords(tags: readonly LuluResponsibility[]): string {
  return tags.map((tag) => t(responsibilityCopyId(tag))).join('、')
}

/** 标签 → 文案 id。闭集在 queries.ts，文案在 docs/copy/lulu_agents.yaml，两边同名。 */
function responsibilityCopyId(tag: LuluResponsibility): `lulu_agents.resp_${LuluResponsibility}` {
  return `lulu_agents.resp_${tag}`
}

/**
 * 建一位 / 改一位，**同一份表**。
 *
 * 分成两份的话，「让 Lulu 配」这类东西迟早只长在其中一份上——
 * 2026-09-06 之前正是这个形状：新增那条有整张表，而已有的 Agent
 * 在界面上**一个字都改不了**（`useUpdateAgent` 写好了，零个消费方，
 * 名单每行只有「停用」）。于是 arron 建的 buger 职责空着、没绑执行器，
 * 一件活都派不到他头上，而他没有任何办法把它补上。
 *
 * 所以这里只有一份表，两种用途只差三样：标题、主按钮、交给谁。
 */
function AgentDialog({
  mode,
  name,
  role,
  responsibilities,
  onName,
  onRole,
  onToggleResponsibility,
  filled,
  filling,
  fillFailed,
  onFill,
  failed,
  busy,
  onCancel,
  onSubmit,
}: {
  mode: 'create' | 'edit'
  name: string
  role: string
  responsibilities: readonly LuluResponsibility[]
  onName: (v: string) => void
  onRole: (v: string) => void
  onToggleResponsibility: (tag: LuluResponsibility) => void
  /** 「让 Lulu 配」那一下的结果。null = 还没配过。 */
  filled: {
    readonly tags: readonly LuluResponsibility[]
    readonly executor: { readonly id: string; readonly name: string } | null
  } | null
  filling: boolean
  fillFailed: boolean
  onFill: () => void
  failed: boolean
  busy: boolean
  onCancel: () => void
  onSubmit: () => void
}) {
  // 那句话是空的就没什么可配的。**灰掉而不是让他点了再说「你没写」**——
  // 一个点了没反应的按钮，他会以为是坏的。
  const canFill = role.trim() !== '' && !filling
  return (
    <section
      data-testid={mode === 'create' ? 'agents-add-dialog' : 'agents-edit-dialog'}
      className="mb-3 rounded-npc-chip border border-npc-lulu-line-2 bg-npc-lulu-bg-2 p-3.5"
    >
      <h2 className="font-npc-display text-npc-section-sm text-npc-lulu-t1">
        {mode === 'create' ? (
          <T id="lulu_agents.dialog_title" />
        ) : (
          t('lulu_agents.edit_dialog_title', { name })
        )}
      </h2>
      <p className="mt-1 text-npc-caption text-npc-lulu-t3">
        <T id={mode === 'create' ? 'lulu_agents.dialog_desc' : 'lulu_agents.edit_dialog_desc'} />
      </p>

      <label className="mt-2.5 block">
        <span className="text-npc-caption text-npc-lulu-t2">
          <T id="lulu_agents.f_name" />
        </span>
        <input
          data-testid="agents-f-name"
          value={name}
          aria-label={t('lulu_agents.f_name')}
          onChange={(e) => onName(e.target.value)}
          className="mt-1 h-[30px] w-full rounded-npc-chip border border-npc-lulu-line-2 bg-npc-lulu-bg-1 px-2.5 text-npc-caption text-npc-lulu-t1 outline-none" /* npc-token-allow: 设计稿输入高 30px */
        />
        <span className="mt-0.5 block text-npc-meta text-npc-lulu-t4">
          <T id="lulu_agents.f_name_hint" />
        </span>
      </label>

      <label className="mt-2 block">
        <span className="text-npc-caption text-npc-lulu-t2">
          <T id="lulu_agents.f_role" />
        </span>
        <input
          data-testid="agents-f-role"
          value={role}
          aria-label={t('lulu_agents.f_role')}
          onChange={(e) => onRole(e.target.value)}
          className="mt-1 h-[30px] w-full rounded-npc-chip border border-npc-lulu-line-2 bg-npc-lulu-bg-1 px-2.5 text-npc-caption text-npc-lulu-t1 outline-none" /* npc-token-allow: 同上 */
        />
        <span className="mt-0.5 block text-npc-meta text-npc-lulu-t4">
          <T id="lulu_agents.f_role_hint" />
        </span>
      </label>

      {/* 「让 Lulu 配」。
          它挨着「他负责什么」放，因为它读的就是上面那一格——
          放到标签下面的话，看起来像在配标签，而它其实是在读那句话。 */}
      <div className="mt-1.5 flex flex-wrap items-center gap-2">
        <button
          type="button"
          data-testid="agents-autofill"
          disabled={!canFill}
          onClick={onFill}
          className="h-[26px] rounded-npc-chip border border-npc-lulu-line-2 bg-npc-lulu-bg-1 px-2.5 text-npc-meta text-npc-lulu-t2 disabled:opacity-40" /* npc-token-allow: 设计稿次级小按钮高 26px */
        >
          <T id={filling ? 'lulu_agents.autofill_working' : 'lulu_agents.autofill'} />
        </button>
        <span className="text-npc-meta text-npc-lulu-t4">
          <T id="lulu_agents.autofill_hint" />
        </span>
      </div>

      {/* 配完说了什么，四档各说各的——**下一个动作不一样，就不合成一句**：
          配上了标签+执行器 = 可以保存了；配上标签但没有执行器 = 保存了也不干活；
          一个都没配出来 = 你自己勾；调用失败 = 跟你没关系，自己勾就行。 */}
      {fillFailed ? (
        <p data-testid="agents-autofill-failed" className="mt-1 text-npc-meta text-npc-lulu-warn">
          <T id="lulu_agents.autofill_failed" />
        </p>
      ) : filled === null ? null : filled.tags.length === 0 ? (
        <p data-testid="agents-autofill-unsure" className="mt-1 text-npc-meta text-npc-lulu-warn">
          <T id="lulu_agents.autofill_unsure" />
        </p>
      ) : filled.executor === null ? (
        <p data-testid="agents-autofill-no-executor" className="mt-1 text-npc-meta text-npc-lulu-warn">
          {t('lulu_agents.autofill_done_no_executor', { tags: tagWords(filled.tags) })}
        </p>
      ) : (
        <p data-testid="agents-autofill-done" className="mt-1 text-npc-meta text-npc-lulu-t3">
          {t('lulu_agents.autofill_done', {
            tags: tagWords(filled.tags),
            executor: filled.executor.name,
          })}
        </p>
      )}

      <fieldset className="mt-2 block border-0 p-0">
        <legend className="text-npc-caption text-npc-lulu-t2">
          <T id="lulu_agents.f_responsibilities" />
        </legend>
        <div data-testid="agents-f-responsibilities" className="mt-1 flex flex-wrap gap-1.5">
          {LULU_RESPONSIBILITIES.map((tag) => {
            const on = responsibilities.includes(tag)
            return (
              <button
                key={tag}
                type="button"
                data-testid={`agents-resp-${tag}`}
                aria-pressed={on}
                onClick={() => onToggleResponsibility(tag)}
                className={
                  on
                    ? 'rounded-npc-chip border border-npc-lulu-t1 bg-npc-lulu-t1 px-2 py-0.5 text-npc-meta text-npc-lulu-bg-0'
                    : 'rounded-npc-chip border border-npc-lulu-line-2 bg-npc-lulu-bg-1 px-2 py-0.5 text-npc-meta text-npc-lulu-t2'
                }
              >
                <T id={responsibilityCopyId(tag)} />
              </button>
            )
          })}
        </div>
        <span className="mt-0.5 block text-npc-meta text-npc-lulu-t4">
          <T id="lulu_agents.f_responsibilities_hint" />
        </span>
        {/* 一个都没勾时说的是**后果**，不是规则。
            「必填」他不知道为什么；「他会一直闲着」他知道。
            这一句是给 buger 那种情况准备的：建好了、看着正常、一件活都接不到。 */}
        {responsibilities.length === 0 ? (
          <span
            data-testid="agents-no-responsibility-warning"
            className="mt-1 block text-npc-meta text-npc-lulu-warn"
          >
            <T id="lulu_agents.f_responsibilities_empty" />
          </span>
        ) : null}
      </fieldset>

      <p className="mt-2 text-npc-meta text-npc-lulu-t4">
        <T id="lulu_agents.identity_note" />
      </p>

      {failed ? (
        <p
          data-testid={mode === 'create' ? 'agents-create-failed' : 'agents-save-failed'}
          className="mt-1 text-npc-meta text-npc-lulu-bad"
        >
          <T id={mode === 'create' ? 'lulu_agents.create_failed' : 'lulu_agents.save_failed'} />
        </p>
      ) : null}

      <div className="mt-2.5 flex gap-2">
        <button
          type="button"
          data-testid={mode === 'create' ? 'agents-create' : 'agents-save'}
          disabled={busy}
          onClick={onSubmit}
          className="h-[30px] rounded-npc-chip bg-npc-lulu-t1 px-3 text-npc-caption font-medium text-npc-lulu-bg-0 disabled:opacity-50" /* npc-token-allow: 同上 */
        >
          <T id={mode === 'create' ? 'lulu_agents.create' : 'lulu_agents.save'} />
        </button>
        <button
          type="button"
          data-testid="agents-cancel"
          onClick={onCancel}
          className="h-[30px] rounded-npc-chip border border-npc-lulu-line-2 px-3 text-npc-caption text-npc-lulu-t2" /* npc-token-allow: 同上 */
        >
          <T id="lulu_agents.cancel" />
        </button>
      </div>
    </section>
  )
}
